import { Component, computed, DestroyRef, HostListener, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaPopoverComponent, RaSkeletonComponent, RaStackedBarComponent, RaStackedBarSegment } from 'ra-ui';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaClickOutsideDirective } from 'src/app/shared/directives';
import { UsageLimitsStore } from 'src/app/shared/services/usage-limits/usage-limits.store';
import { UsageHumanize } from 'src/app/modules/usage/pages/usage/usage-humanize.util';
import { RaEditTooltipDirective } from '../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';

/**
 * Limites de uso no header do editor: a pílula mostra quanto do plano já foi
 * consumido (barra por categoria + %) e abre um popover com o detalhe: créditos
 * do período, renovação, o gasto do "Editar com IA" e o saldo. Atualiza ao vivo
 * a cada gasto (ver `UsageLimitsStore`).
 */
@Component({
    selector: 'usage-limits-pill',
    standalone: true,
    imports: [
        TranslatePipe,
        RaIconComponent,
        RaPopoverComponent,
        RaStackedBarComponent,
        RaSkeletonComponent,
        RaClickOutsideDirective,
        RaEditTooltipDirective,
    ],
    templateUrl: './usage-limits-pill.component.html',
    styleUrl: './usage-limits-pill.component.scss',
})
export class UsageLimitsPillComponent {
    public readonly store = inject(UsageLimitsStore);
    private readonly _router = inject(Router);
    private readonly _translate = inject(TranslateService);
    private readonly _destroyRef = inject(DestroyRef);

    public readonly UsageHumanize = UsageHumanize;
    public readonly open = signal(false);

    /** Fatias da barra: uma por categoria que gastou, na cor da categoria. */
    public readonly segments = computed<RaStackedBarSegment[]>(() =>
        this.store.categories().map(category => ({
            value: category.sumTokens,
            color: UsageHumanize.categoryColor(category.category),
            label: `${this._translate.instant(UsageHumanize.categoryLabelKey(category.category))} · ${UsageHumanize.formatTokens(category.sumTokens)}`,
        }))
    );

    public readonly renewDateText = computed(() => UsageHumanize.formatDate(this.store.renewDate() ?? undefined));

    constructor() {
        this.store.start();
        this._destroyRef.onDestroy(() => this.store.stop());
    }

    public toggle(): void {
        const next = !this.open();
        this.open.set(next);
        // Abrir é um bom momento para conferir: barato, e garante o número mais recente.
        if (next && this.store.summary()) this.store.refresh(true);
    }

    public close(): void {
        this.open.set(false);
    }

    public retry(): void {
        this.store.refresh(false);
    }

    public goToUsage(): void {
        this.close();
        this._router.navigate(['/platform/usage']);
    }

    public goToPlans(): void {
        this.close();
        this._router.navigate(['/platform/plans']);
    }

    @HostListener('document:keydown.escape')
    protected onEscape(): void {
        if (this.open()) this.close();
    }
}
